import {
  getDataCenterDataById,
  useGetTableDataInfinite,
} from "@/queries/data-center/data-center.api";
import { cn } from "@/utils/theme";
import { useDebounceHook } from "@/hooks/use-debounce";
import {
  Button,
  Checkbox,
  Icon,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@elaniintc/goes-design-system";
import { useState, useEffect, useMemo } from "react";

interface DataCenterDataMultiselectProps {
  disabled?: boolean;
  placeholder?: string;
  initOptions?: string[];
  value?: Array<{ key: string; label: string; table?: string }>;
  onChange?: (
    value: Array<{ key: string; label: string; table?: string }>,
  ) => void;
  onBlur?: () => void;
  group?: "left" | "right" | "single" | "down-left" | "down-right";
  database?: {
    table: string | null | undefined;
    column: string | null | undefined;
  };
  className?: string;
  wrapperClassName?: string;
  enableSearch?: boolean;
}

export const DataCenterDataMultiselect = ({
  disabled,
  placeholder = "Selecciona una opción",
  initOptions,
  value,
  onChange,
  onBlur,
  group,
  database,
  wrapperClassName,
  className,
  enableSearch,
}: DataCenterDataMultiselectProps) => {
  const [options, setOptions] = useState<
    { key: string; label: string; table?: string }[]
  >(
    initOptions
      ?.filter(Boolean)
      .map((option) => ({ key: option, label: option })) || [],
  );
  const [searchString, setSearchString] = useState("");
  const debouncedSearch = useDebounceHook(searchString, 300);

  // Use infinite query for database-driven selects
  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingInfinite,
  } = useGetTableDataInfinite({
    name: database?.table || "",
    search: debouncedSearch,
    per_page: 15,
  });

  // Transform infinite query data to options
  const databaseOptions = useMemo(() => {
    if (!database?.table || !database?.column || !infiniteData) return [];

    const column_name = database.column;
    const table_name = database.table;

    return infiniteData.pages.flatMap((page) =>
      (page.data?.data || []).map(
        (item: { [key: string]: string | number }) => ({
          key: item.id as string,
          label: item[column_name] as string,
          table: table_name,
        }),
      ),
    );
  }, [infiniteData, database?.table, database?.column]);

  // Set options based on database or initial options
  useEffect(() => {
    if (database?.table) {
      setOptions(databaseOptions);
    } else if (initOptions && !database?.table) {
      setOptions(
        initOptions.filter(Boolean).map((option) => ({
          key: option,
          label: option,
        })),
      );
    }
  }, [databaseOptions, initOptions, database?.table]);

  // Normalize value when options are loaded from database and value contains only IDs
  useEffect(() => {
    if (!onChange) return;
    if (!database?.table || !database?.column) return;
    if (!value) return;
    if (!Array.isArray(value)) return;
    if (options.length === 0) return;

    const hasStringValues = value.every((v) => typeof v === "string");

    if (!hasStringValues) {
      const needsNormalization =
        value.length > 0 && value[0].key === value[0].label;
      if (!needsNormalization) return;

      const normalizedValue = value
        .map((v) => {
          const id = typeof v === "string" ? v : v.key;
          return options.find((opt) => opt.key === id);
        })
        .filter(
          (opt): opt is { key: string; label: string; table?: string } =>
            opt !== undefined,
        );

      // Only update if the normalized value is actually different
      if (JSON.stringify(normalizedValue) !== JSON.stringify(value)) {
        onChange(normalizedValue);
      }
    } else {
      // Value contiene solo strings (IDs)
      const optionsFound = options.filter((opt) =>
        value.some((v) => v === opt.key),
      );

      if (optionsFound.length > 0 && optionsFound.length === value.length) {
        // Todas las opciones fueron encontradas
        onChange(optionsFound);
      } else {
        // Buscar las opciones faltantes en la base de datos
        const missingIds = value.filter(
          (id) => !options.some((opt) => opt.key === id),
        );

        if (missingIds.length === 0) return;

        const fetchMissingOptions = async () => {
          try {
            const table_name = database.table!;
            const column_name = database.column!;
            const fetchedOptions: {
              key: string;
              label: string;
              table?: string;
            }[] = [];

            // Obtener cada opción faltante individualmente
            for (const id of missingIds) {
              try {
                const response = await getDataCenterDataById(table_name, id);
                if (
                  response &&
                  response.data &&
                  Array.isArray(response.data) &&
                  response.data.length > 0
                ) {
                  const item = response.data[0];
                  fetchedOptions.push({
                    key: item.id,
                    label: item[column_name],
                    table: table_name,
                  });
                }
              } catch (error) {
                console.error(`Error fetching option ${id}:`, error);
              }
            }

            if (fetchedOptions.length > 0) {
              // Filtrar duplicados antes de agregar
              setOptions((prevOptions) => {
                const existingKeys = new Set(prevOptions.map((opt) => opt.key));
                const uniqueOptions = fetchedOptions.filter(
                  (opt) => !existingKeys.has(opt.key),
                );
                return [...uniqueOptions, ...prevOptions];
              });

              // Actualizar el valor con las opciones completas
              const allOptions = [...options, ...fetchedOptions];
              const updatedValue = value
                .map((v) => allOptions.find((opt) => opt.key === v))
                .filter(
                  (
                    opt,
                  ): opt is { key: string; label: string; table?: string } =>
                    opt !== undefined,
                );
              onChange(updatedValue);
            }
          } catch (error) {
            console.error(`Error fetching missing options:`, error);
          }
        };

        fetchMissingOptions();
      }
    }
  }, [options, value, onChange, database?.table, database?.column]);

  // Filter options locally when not using database
  const filteredOptions = useMemo(() => {
    // For database-driven selects, options are already filtered by API
    if (database?.table) {
      return options;
    }
    // For local options, filter client-side
    if (!searchString.trim()) {
      return options;
    }
    const search = searchString.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(search));
  }, [options, searchString, database?.table]);

  const SearchBox = enableSearch ? (
    <div className="border-b border-neutral-quiet p-3 relative ">
      <Icon
        type="far"
        name="search"
        className="absolute left-5 top-1/2 transform -translate-y-1/2 text-body"
      />
      <Input
        variant="sm"
        className="text-sm w-full font-normal flex-auto pl-9"
        placeholder={"Buscar..."}
        onChange={(e) => setSearchString(e.target.value)}
        value={searchString}
        onKeyDown={(e) => e.stopPropagation()}
      />
    </div>
  ) : null;

  return (
    <div className={cn("min-w-0 flex-1 h-full ", wrapperClassName)}>
      <Popover onOpenChange={(open) => !open && onBlur?.()}>
        <PopoverTrigger asChild disabled={disabled}>
          <Button
            group={group}
            variant="tertiary"
            className={cn(
              "w-full h-full justify-between group items-center min-h-10 px-2 relative",
              "text-body-quiet leading-3xl font-normal",
              className,
            )}
            disabled={disabled}
          >
            {isLoadingInfinite ? (
              <span>Cargando...</span>
            ) : (
              <span className="truncate pr-6">
                {value && Array.isArray(value) && value.length > 0
                  ? value
                      .map((v) => (typeof v === "object" ? v.label : v))
                      .join(", ")
                  : placeholder}
              </span>
            )}
            {!isLoadingInfinite ? (
              <Icon
                type="far"
                name="angle-down"
                className="shrink-0 group-data-[state=open]:rotate-180 transition-transform duration-200 group-data-[state=open]:text-body absolute right-3 top-1/2 transform -translate-y-1/2"
              />
            ) : (
              <Icon
                name="spinner-third"
                type="fas"
                className="ml-auto shrink-0 animate-spin"
              />
            )}
          </Button>
        </PopoverTrigger>
        {!disabled && !isLoadingInfinite && (
          <PopoverContent className="p-2 shadow-md w-(--radix-popover-trigger-width)">
            {SearchBox}
            <div className="max-h-48 overflow-y-auto flex flex-col gap-1">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <label
                    key={option.key}
                    className="flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-gray-100 rounded text-body-quiet"
                  >
                    <Checkbox
                      checked={value?.some(
                        (v) =>
                          (typeof v === "string"
                            ? v
                            : (v as { key: string; label: string }).key) ===
                          option.key,
                      )}
                      onChange={() => {
                        let newValue: Array<{ key: string; label: string }>;
                        const isSelected = value?.some(
                          (v) =>
                            (typeof v === "string"
                              ? v
                              : (v as { key: string; label: string }).key) ===
                            option.key,
                        );
                        if (isSelected) {
                          newValue =
                            value?.filter(
                              (v) =>
                                (typeof v === "string"
                                  ? v
                                  : (v as { key: string; label: string })
                                      .key) !== option.key,
                            ) || [];
                        } else {
                          newValue = [...(value ?? []), option];
                        }
                        onChange?.(newValue);
                      }}
                      disabled={disabled}
                    />
                    {option.label}
                  </label>
                ))
              ) : (
                <div className="flex items-center justify-center min-h-40 px-4">
                  <p className="text-body-quiet text-base font-normal text-center">
                    {searchString.trim().length > 0
                      ? "No se encontraron resultados"
                      : "No hay opciones disponibles"}
                  </p>
                </div>
              )}
              {hasNextPage && (
                <div className="w-full mt-2">
                  <Button
                    variant="ghost-blue"
                    size="sm"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? (
                      <>
                        <Icon
                          name="spinner-third"
                          type="fas"
                          className="animate-spin mr-2"
                        />
                        Cargando...
                      </>
                    ) : (
                      <>
                        Cargar más
                        <Icon name="add" type="fas" className="ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </PopoverContent>
        )}
      </Popover>
    </div>
  );
};
